import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import ErrorMessage from '@/components/ui/ErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const AuthConfirm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState<string>('Verifying your email...');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendMessage, setResendMessage] = useState<string>('');

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token_hash = params.get('token_hash') || '';
  const type = (params.get('type') || 'signup') as
    | 'signup'
    | 'email'
    | 'magiclink'
    | 'recovery'
    | 'invite'
    | 'email_change';

  useEffect(() => {
    document.title = 'Confirming your email | Flamingo';
    const run = async () => {
      try {
        if (!token_hash || !type) {
          setStatus('error');
          setMessage('Invalid confirmation link.');
          return;
        }

        // Verify the token with Supabase and establish a session if applicable
        const { data, error } = await supabase.auth.verifyOtp({ token_hash, type });
        if (error) {
          console.warn('[AuthConfirm] verifyOtp error', { type, token_hash_present: !!token_hash, message: error.message, status: (error as any)?.status, code: (error as any)?.code });
          // Fallback: if a session already exists, proceed
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData?.session?.user) {
            setStatus('success');
            setMessage('Verified! Loading your dashboard...');
          } else {
            setStatus('error');
            const lower = (error.message || '').toLowerCase();
            const friendly = (error as any)?.code === 'otp_expired' || lower.includes('expired')
              ? 'This confirmation link has expired. Request a new one below.'
              : (lower.includes('not found') || lower.includes('invalid'))
                ? 'This confirmation link is invalid or already used.'
                : 'We could not verify your email. Please try again or request a new link below.';
            setMessage(friendly);
            return;
          }
        }

        // On success, fetch profile to determine the correct dashboard
        const userId = data.session?.user?.id || (await supabase.auth.getUser()).data.user?.id;
        if (!userId) {
          // Email may be confirmed without session; fallback to login
          setStatus('success');
          setMessage('Email confirmed. Redirecting to sign in...');
          setTimeout(() => navigate('/login', { replace: true }), 1200);
          return;
        }

        // Wait for auth state to propagate before redirecting
        setStatus('success');
        setMessage('Verified! Loading your dashboard...');
        
        // Delay to let auth context update
        await new Promise(resolve => setTimeout(resolve, 1000));

        const { data: profile } = await supabase
          .from('profiles')
          .select('user_type')
          .eq('id', userId)
          .single();

        const nextPath = profile?.user_type === 'landlord' ? '/landlord-dashboard' : '/dashboard';
        navigate(nextPath, { replace: true });
      } catch (err: any) {
        console.error('[AuthConfirm] exception', err);
        setStatus('error');
        setMessage(err?.message || 'Something went wrong during verification.');
      }
    };

    void run();
  }, [navigate, token_hash, type]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-background">
      <section className="text-center px-4">
        <div className="mb-6">
          <img
            src="/lovable-uploads/76503acc-0290-4b3e-ae3b-f6ab214ab2ce.png"
            alt="Flamingo logo"
            className="h-8 mx-auto"
            loading="lazy"
          />
        </div>
        {status === 'verifying' && (
          <>
            <LoadingSpinner />
            <p className="text-sm text-muted-foreground mt-3">{message}</p>
          </>
        )}
        {status === 'success' && (
          <p className="text-sm text-muted-foreground mt-3">{message}</p>
        )}
        {status === 'error' && (
          <div className="max-w-md mx-auto">
            <ErrorMessage title="Email verification failed" message={message} showRetry={false} />
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  aria-label="Email address to resend confirmation"
                />
                <Button
                  onClick={async () => {
                    if (!resendEmail || !/^([^\s@]+)@([^\s@]+)\.([^\s@]+)$/.test(resendEmail)) {
                      setResendStatus('error');
                      setResendMessage('Enter a valid email address.');
                      return;
                    }
                    setResendStatus('sending');
                    setResendMessage('');
                    const { error } = await supabase.auth.resend({ type: 'signup', email: resendEmail });
                    if (error) {
                      setResendStatus('error');
                      setResendMessage(error.message || 'Could not resend confirmation email.');
                    } else {
                      setResendStatus('sent');
                      setResendMessage('Confirmation email sent. Please check your inbox.');
                    }
                  }}
                  disabled={resendStatus === 'sending'}
                >
                  {resendStatus === 'sending' ? 'Sending...' : 'Resend link'}
                </Button>
              </div>
              {resendMessage && (
                <p className={`text-xs ${resendStatus === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>{resendMessage}</p>
              )}
              <div className="pt-2">
                <Button variant="outline" onClick={() => navigate('/login')}>Go to login</Button>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default AuthConfirm;
